/**
 * "Updated Methodology · Undo" — the one-line notice for an AI edit the user
 * asked for (R1 W2). The edit is already saved and the editor stays editable;
 * Undo puts the earlier text back unless the section changed since.
 *
 * Two placements share one row: under the panel reply that made the edit, and
 * above the editor for the section it changed.
 */

import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { undoAppliedAiEdit } from '@/lib/ai-edit-actions'
import {
  EDIT_APPLIED,
  EDIT_NOTICE_DISMISS,
  EDIT_UNDO,
  EDIT_UNDO_CONFLICT,
  EDIT_UNDO_FAILED,
  EDIT_UNDO_GONE,
  EDIT_UNDO_RETRY,
  EDIT_UNDONE,
  EDIT_UNDOING,
  UNTITLED_SECTION,
  fillCopy,
} from '@/lib/ai-edit-copy'
import { useAiEditsStore, type AppliedAiEdit } from '@/stores/ai-edits-store'
import { pickEditorEdit } from '@/lib/ai-edit-view'

// ===================
// Row
// ===================

interface AppliedEditLineProps {
  edit: AppliedAiEdit
  onUndo: (proposalId: string) => void
  className?: string
}

export function AppliedEditLine({ edit, onUndo, className }: AppliedEditLineProps) {
  const heading = edit.heading ?? UNTITLED_SECTION
  const undoButton = (label: string) => (
    <Button
      type="button"
      variant="link"
      className="h-auto p-0 text-xs"
      disabled={edit.status === 'undoing'}
      onClick={() => onUndo(edit.proposalId)}
    >
      {label}
    </Button>
  )

  if (edit.status === 'undone') {
    return <p className={cn('text-meta', className)} role="status">{fillCopy(EDIT_UNDONE, { heading })}</p>
  }
  if (edit.status === 'conflict') {
    return <p className={cn('text-meta', className)} role="status">{fillCopy(EDIT_UNDO_CONFLICT, { heading })}</p>
  }
  if (edit.status === 'gone') {
    return <p className={cn('text-meta', className)} role="status">{fillCopy(EDIT_UNDO_GONE, { heading })}</p>
  }
  if (edit.status === 'failed') {
    return (
      <p className={cn('text-meta flex items-center gap-1', className)} role="status">
        <span>{fillCopy(EDIT_UNDO_FAILED, { heading })}</span>
        <span aria-hidden="true">·</span>
        {undoButton(EDIT_UNDO_RETRY)}
      </p>
    )
  }
  return (
    <p className={cn('text-meta flex items-center gap-1', className)}>
      <span className="truncate">{fillCopy(EDIT_APPLIED, { heading })}</span>
      <span aria-hidden="true">·</span>
      {edit.status === 'undoing' ? <span>{EDIT_UNDOING}</span> : undoButton(EDIT_UNDO)}
    </p>
  )
}

// ===================
// Panel placement — under the reply that made the edit
// ===================

export function PanelAppliedEditLines({ messageId }: { messageId: string }) {
  const edits = useAiEditsStore((s) => s.edits)
  const mine = edits.filter((edit) => edit.messageId === messageId)
  if (mine.length === 0) return null
  return (
    <div className="mt-1 flex flex-col gap-0.5" data-testid="applied-edit-lines">
      {mine.map((edit) => (
        <AppliedEditLine key={edit.proposalId} edit={edit} onUndo={(id) => void undoAppliedAiEdit(id)} />
      ))}
    </div>
  )
}

// ===================
// Editor placement — above the section the edit changed
// ===================

interface EditorAppliedEditNoticeProps {
  /** The segments this editor is showing (one, or every segment of a document). */
  visibleSegments: ReadonlyArray<{ id: string; sectionId: string }>
}

export function EditorAppliedEditNotice({ visibleSegments }: EditorAppliedEditNoticeProps) {
  const edits = useAiEditsStore((s) => s.edits)
  const dismissEdit = useAiEditsStore((s) => s.dismissEdit)
  const edit = pickEditorEdit(edits, visibleSegments)
  if (!edit) return null
  return (
    <div
      className="flex items-center justify-between gap-3 px-3 py-1.5 border-b bg-muted/40"
      data-testid="editor-applied-edit-notice"
    >
      <AppliedEditLine edit={edit} onUndo={(id) => void undoAppliedAiEdit(id)} className="min-w-0" />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-6 w-6 shrink-0"
        aria-label={EDIT_NOTICE_DISMISS}
        onClick={() => dismissEdit(edit.proposalId)}
      >
        <X className="h-3.5 w-3.5 text-subtle" />
      </Button>
    </div>
  )
}
